/*
    NOTE: This file is used by import worker don't import any DOM related stuff
    incase DOM things needs to be imported use isWorkerThread util
*/
// @flow
import { ApiError, AbortError, ErrorWithAutoAcknowldge } from './Errors';
import _ from 'lodash';
import DataUtils from 'common/utils/DataUtils';
import I18nHelper from 'common/helpers/I18nHelper';
import warning from 'common/utils/warning';
import { ErrorMessageParser } from './ErrorMessageParser';

export type Method = 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE' | 'HEAD';
type Headers = { [name: string]: string };
type Data = FormData | { [string]: $FixMe };
type Body = FormData | string;
type Request = {
    url: string,
    options: {
        method: Method,
        headers: Headers,
        signal?: AbortSignal,
        body?: Body
    }
};
type Interceptors = {
    [string | number]: (req: Request, resp: Response) => Promise<Response>
};

const fetchWrap = function (defaultHeaders, interceptors) {
    const checkAbortError = error => {
        // AbortError happens when a network request by Fetch interface is aborted forcefully using AbortController
        // https://developers.google.com/web/updates/2017/09/abortable-fetch#reacting_to_an_aborted_fetch
        if (error.name === 'AbortError') {
            throw new AbortError(error.message, error.name);
        }
    };
    const checkNetwork = function (response) {
        checkAbortError(response);
        const error = new ErrorWithAutoAcknowldge(
            I18nHelper.translate('translations', 'networkError'),
            5000
        );
        error.response = response;
        throw error;
    };

    const checkStatus = async function (apiResponse, apiRequest) {
        let response = apiResponse;
        if (interceptors.all) {
            response = await interceptors.all(apiRequest, response);
        }
        if (interceptors[response.status]) {
            response = await interceptors[response.status](
                apiRequest,
                response
            );
        }
        if (response.ok) {
            return response;
        }
        return response.json().then(
            json => {
                const apiError = new ApiError(
                    ErrorMessageParser.getTranslatedMessage(json),
                    response.status
                );
                apiError.data = json;
                throw apiError;
            },
            error => {
                const apiError = new ApiError(
                    response.statusText,
                    response.status
                );
                apiError.data = error;
                throw apiError;
            }
        );
    };

    // @TODO: for now keeping return type Promise<$FixMe>
    // to properly type we have to add generic type
    return function proFetch(
        url: string,
        options: {
            method?: Method,
            headers?: Headers,
            body?: Body,
            signal?: AbortSignal
        }
    ): Promise<$FixMe> {
        const request: Request = {
            url,
            options: {
                method: options.method || 'GET',
                headers: {
                    ...defaultHeaders,
                    ...options.headers
                },
                body: options.body,
                signal: options.signal
            }
        };

        return fetch(request.url, { ...request.options })
            .then(response => checkStatus(response, request), checkNetwork)
            .then(response => {
                const contentType = response.headers.get('Content-Type');
                if (
                    contentType &&
                    contentType.indexOf('application/json') !== -1
                ) {
                    return response.json();
                }
                return response;
            })
            .catch(error => {
                // Handling again here as reading the response of rejected request
                // will also throw AbortError which will not be handled in checkNetwork
                checkAbortError(error);
                throw error;
            })
            .catch(error => {
                const handledErrors = [
                    ApiError,
                    AbortError,
                    ErrorWithAutoAcknowldge
                ];
                const isHandledError = handledErrors.some(
                    ErrorClass => error instanceof ErrorClass
                );

                if (!isHandledError) {
                    warning(false, error.message, {
                        metadata: {
                            // NOTE: headers can have token, so not sending to bugsnag
                            request: _.omit(request, 'options.headers'),
                            message: error.message,
                            statusCode: error.statusCode,
                            name: error.name
                        }
                    });
                }
                throw error;
            });
    };
};

export default function createHttpAdapter(
    defaultHeaders: Headers = {},
    interceptors: Interceptors = {}
) {
    const proFetch = fetchWrap(defaultHeaders, interceptors);

    const processData = data => {
        if (data instanceof FormData) {
            return data;
        }
        return JSON.stringify(data);
    };

    const processHeader = (data, header) => {
        if (DataUtils.isValidJSON(data) && !(data instanceof FormData)) {
            return _.merge({}, header, { 'Content-Type': 'application/json' });
        }

        return header;
    };

    const get = function (
        url: string,
        headers?: Headers,
        abortSignal?: AbortSignal
    ) {
        return proFetch(url, { headers, signal: abortSignal });
    };

    const head = function (
        url: string,
        headers?: Headers,
        abortSignal?: AbortSignal
    ) {
        return proFetch(url, { method: 'HEAD', headers, signal: abortSignal });
    };

    const put = function (
        url: string,
        data: Data,
        headers?: Headers,
        abortSignal?: AbortSignal
    ) {
        const requestHeaders = processHeader(data, headers);
        return proFetch(url, {
            method: 'PUT',
            headers: requestHeaders,
            body: processData(data),
            signal: abortSignal
        });
    };

    const post = function (
        url: string,
        data: Data,
        headers?: Headers,
        abortSignal?: AbortSignal
    ) {
        const requestHeaders = processHeader(data, headers);
        return proFetch(url, {
            method: 'POST',
            headers: requestHeaders,
            body: processData(data),
            signal: abortSignal
        });
    };

    const _delete = function (
        url: string,
        headers?: Headers,
        abortSignal?: AbortSignal
    ) {
        return proFetch(url, {
            method: 'DELETE',
            headers,
            signal: abortSignal
        });
    };

    const patch = function (
        url: string,
        data: Data,
        headers?: Headers,
        abortSignal?: AbortSignal
    ) {
        const requestHeaders = processHeader(data, headers);
        return proFetch(url, {
            method: 'PATCH',
            headers: requestHeaders,
            body: processData(data),
            signal: abortSignal
        });
    };

    return {
        get,
        put,
        post,
        head,
        delete: _delete,
        patch
    };
}
